"use client";

import { useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

const SPEED = 70;

export default function HeroTicker({ words, className }: { words?: string[]; className?: string }) {
  const stripRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [repeat, setRepeat] = useState(1);
  const [duration, setDuration] = useState(0);
  const items = words?.filter(Boolean) ?? [];

  useLayoutEffect(() => {
    const strip = stripRef.current;
    const list = listRef.current;
    if (!strip || !list) return;
    const measure = () => {
      const listWidth = list.offsetWidth / repeat;
      if (!listWidth) return;
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 8;
      const next = Math.max(1, Math.ceil(strip.offsetWidth / listWidth));
      setRepeat(next);
      setDuration((listWidth * next) / ((SPEED * rem) / 8));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [repeat, items.length]);

  if (!items.length) return null;

  const group = (hidden: boolean) => (
    <div
      ref={hidden ? undefined : listRef}
      aria-hidden={hidden || undefined}
      className="flex shrink-0 items-center">
      {Array.from({ length: repeat }, (_, r) =>
        items.map((word, i) => (
          <div
            key={`${r}-${i}`}
            className="flex shrink-0 items-center gap-[6rem] pr-[6rem]">
            <span className="whitespace-nowrap font-geist text-[2rem] font-[500] uppercase leading-[1.146] text-black/60">
              {word}
            </span>
            <span
              aria-hidden
              className="h-[0.5rem] w-[0.5rem] shrink-0 bg-[#228E71]"
            />
          </div>
        )),
      )}
    </div>
  );

  return (
    <div
      ref={stripRef}
      className={cn("flex items-center overflow-hidden", className)}>
      <div
        className="riz-hero-ticker flex w-max shrink-0 max-lg:ml-[-4.875rem] lg:ml-[-4.75rem]"
        style={duration ? { animationDuration: `${duration}s` } : { animationPlayState: "paused" }}>
        {group(false)}
        {group(true)}
      </div>
      <style jsx>{`
        .riz-hero-ticker {
          animation: riz-hero-ticker 40s linear infinite;
        }
        @keyframes riz-hero-ticker {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .riz-hero-ticker {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
